import React, { useState, useEffect } from 'react';
import { ExternalLink, Building2, Clock } from 'lucide-react';
import { Button } from './ui/button';

const Portfolio = () => {
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/case-studies`);
        if (response.ok) {
          const data = await response.json();
          setCaseStudies(data);
        }
      } catch (error) {
        console.error('Failed to fetch case studies:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCaseStudies();
  }, []);

  return (
    <section id="portfolio" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Success <span className="text-blue-500">Stories</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Real results from real clients across industries
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            <p className="mt-4 text-slate-600">Loading case studies...</p>
          </div>
        ) : caseStudies.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-600">No case studies available yet.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
            <div
              key={study.id}
              className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-blue-300 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
              onClick={() => setSelectedCase(study)}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={study.image}
                  alt={study.client}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="inline-block px-3 py-1 bg-blue-500 text-white text-xs font-semibold rounded-full">
                    {study.industry}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-500 transition-colors duration-300">
                  {study.client}
                </h3>
                <p className="text-sm text-blue-600 font-semibold mb-3">{study.service}</p>
                <p className="text-slate-600 text-sm mb-4 line-clamp-2">{study.challenge}</p>

                <div className="flex items-center justify-between text-sm text-slate-500">
                  <div className="flex items-center">
                    <Clock size={16} className="mr-1" />
                    <span>{study.duration}</span>
                  </div>
                  <ExternalLink size={16} className="text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </div>
            </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal for case study details */}
      {selectedCase && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedCase(null)}
          >
            <div
              className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative h-64">
                <img
                  src={selectedCase.image}
                  alt={selectedCase.client}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => setSelectedCase(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors duration-300"
                >
                  ✕
                </button>
              </div>

              <div className="p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <span className="px-3 py-1 bg-blue-100 text-blue-600 text-sm font-semibold rounded-full">
                    {selectedCase.industry}
                  </span>
                  <span className="flex items-center text-slate-500 text-sm">
                    <Clock size={16} className="mr-1" />
                    {selectedCase.duration}
                  </span>
                </div>

                <h2 className="text-3xl font-bold text-slate-900 mb-2">{selectedCase.client}</h2>
                <p className="text-lg text-blue-600 font-semibold mb-6">{selectedCase.service}</p>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2 flex items-center">
                      <Building2 size={20} className="mr-2 text-blue-500" />
                      Challenge
                    </h3>
                    <p className="text-slate-600 leading-relaxed">{selectedCase.challenge}</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Solution</h3>
                    <p className="text-slate-600 leading-relaxed">{selectedCase.solution}</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">Results</h3>
                    <ul className="space-y-2">
                      {selectedCase.results.map((result, idx) => (
                        <li key={idx} className="flex items-start text-slate-700">
                          <span className="text-green-500 mr-2 mt-1 flex-shrink-0">✓</span>
                          <span>{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <Button
                  onClick={() => {
                    setSelectedCase(null);
                    const element = document.getElementById('contact');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="w-full mt-8 bg-blue-500 hover:bg-blue-600 text-white py-6 text-lg"
                >
                  Start Your Success Story
                </Button>
              </div>
            </div>
          </div>
        )}
    </section>
  );
};

export default Portfolio;